/**
 * Hook del listado de Aliados con paginación server-side (US-2.2).
 *
 * Patrón de referencia para los listados del Admin Web: filtros + `offset`/`limit` +
 * `meta` del backend, con loading/error/refresh. La red vive en `allyApi`; aquí solo
 * el estado de la vista (separable de la presentación).
 */
import { useCallback, useEffect, useState } from 'react';
import type {
  AllyDocumentType,
  AllyStatus,
  AllySummary,
  PageMeta,
  PersonType,
} from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { allyApi, type AllyListParams } from '../api/allyApi';

/** Límite por página por defecto del Admin Web. */
export const DEFAULT_ALLIES_LIMIT = 20;

/** Filtros del listado (los del contrato, sin paginación). */
export interface AllyFilters {
  q?: string;
  personType?: PersonType;
  documentType?: AllyDocumentType;
  documentNumber?: string;
  status?: AllyStatus;
}

export interface UseAlliesOptions {
  /** Elementos por página (default `DEFAULT_ALLIES_LIMIT`). */
  limit?: number;
  /** Filtros iniciales. */
  initialFilters?: AllyFilters;
}

export interface UseAlliesResult {
  /** Items resumidos del listado (sin `admins`; el detalle los trae). */
  data: AllySummary[];
  meta: PageMeta;
  loading: boolean;
  error: string | null;
  /** Filtros activos. */
  filters: AllyFilters;
  /** Desplazamiento actual (paginación server-side). */
  offset: number;
  limit: number;
  /** Aplica filtros nuevos y reinicia la paginación a la primera página. */
  setFilters: (filters: AllyFilters) => void;
  /** Mueve la página (offset) actual. */
  setOffset: (offset: number) => void;
  /** Recarga la página actual (p. ej. tras crear/editar/eliminar un aliado). */
  refresh: () => void;
}

const emptyMeta = (limit: number): PageMeta => ({ offset: 0, limit, total: 0, hasMore: false });

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudieron cargar los aliados.';
}

export function useAllies(options: UseAlliesOptions = {}): UseAlliesResult {
  const limit = options.limit ?? DEFAULT_ALLIES_LIMIT;
  const [filters, setFiltersState] = useState<AllyFilters>(() => options.initialFilters ?? {});
  const [offset, setOffsetState] = useState(0);
  const [data, setData] = useState<AllySummary[]>([]);
  const [meta, setMeta] = useState<PageMeta>(() => emptyMeta(limit));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params: AllyListParams = { ...filters, offset, limit };
    allyApi
      .list(params)
      .then((page) => {
        if (cancelled) return;
        setData(page.data);
        setMeta(page.meta);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(toErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Descarta respuestas de una página/filtro anterior que lleguen fuera de orden.
    return () => {
      cancelled = true;
    };
  }, [filters, offset, limit, reloadKey]);

  const setFilters = useCallback((next: AllyFilters) => {
    setOffsetState(0);
    setFiltersState(next);
  }, []);

  const setOffset = useCallback((next: number) => {
    setOffsetState(next < 0 ? 0 : next);
  }, []);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return { data, meta, loading, error, filters, offset, limit, setFilters, setOffset, refresh };
}
